/**
 * Typed client for the agitome API. One file, shared by all four surfaces.
 *
 * Four things about this API are easy to get wrong, so they are encoded here once:
 *
 *  1. **`reported` and `verified` are different claims about the world.** Every count that
 *     carries both keeps them apart, and nothing in this client ever sums them. That distinction
 *     is the whole product; a helper that added them would be the one bug worth most.
 *  2. **`insufficient` is not the safe end of the tier ladder** — it is the absence of an
 *     assessment. `TIER_ORDER` deliberately does not contain it, so anything sorting by tier
 *     cannot place it next to `low`.
 *  3. **Money is a decimal string on the wire**, never a float. It stays a string end to end
 *     here; we never parseFloat it.
 *  4. **Numbers are formatted with an explicit locale.** `toLocaleString()` with no argument
 *     asks the runtime, and the prerenderer's runtime is Node while the reader's is a browser —
 *     so "1,284" and "1284" hydrate as a text mismatch and React discards the server HTML for
 *     the whole subtree. Every call in this UI passes "en-US".
 *  5. **The public half needs no key.** `publicGet`/`publicPost` send no Authorization header at
 *     all, so a GTM page cannot accidentally acquire a tenant's authority by importing the wrong
 *     helper — the two halves of this file are two functions rather than one with a flag.
 */

// ── configuration ──────────────────────────────────────────────────────────

/** Where the API lives. Dev proxies `/api`; production sets it at build time. */
export const API_BASE: string =
  (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE ?? "/api"

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

async function unwrap(resp: Response): Promise<unknown> {
  if (resp.ok) return resp.status === 204 ? null : await resp.json()
  // Every failure in this API is `{"error": {"code", "message"}}` — including 422, which FastAPI
  // would otherwise answer as `{"detail": [...]}`. So there is one shape to read and the message
  // is always a sentence rather than a serialized list.
  let code = "error"
  let message = `${resp.status} ${resp.statusText}`
  try {
    const body = (await resp.json()) as { error?: { code?: string; message?: string } }
    if (body.error) {
      code = body.error.code ?? code
      message = body.error.message ?? message
    }
  } catch {
    /* a body that is not JSON tells us nothing the status has not already */
  }
  throw new ApiError(code, message, resp.status)
}

function query(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ""
}

// ── the public half: no credential, ever ───────────────────────────────────

export async function publicGet<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  return (await unwrap(await fetch(`${API_BASE}${path}${query(params)}`))) as T
}

export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  return (await unwrap(
    await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  )) as T
}

// ── the authenticated half ─────────────────────────────────────────────────

/**
 * THE KEY IS NO LONGER HELD HERE, and that is the whole change.
 *
 * It used to live in `localStorage`, and the comment defending that argued against the wrong
 * threat — "never in a URL, because a key in a query string ends up in browser history" — which
 * is true and is not the exposure that matters. Anything executing on this origin could read it.
 *
 * It was worse here than in authoxi. agitome issues no credentials of its own, so the key in this
 * browser is an **authoxi** key: an XSS on app.agitome.com did not compromise agitome, it
 * compromised the tenant's authoxi account and every product that trusts it. The staff console
 * was worse again — it holds `adjudicate` and `leads:read`, which reach other people's disputes
 * and other people's email addresses.
 *
 * The key is now posted once to `/v1/bff/session`, kept server-side, and this browser holds an
 * `HttpOnly` cookie it cannot read. Every authenticated call goes through `/v1/bff/api/*` on this
 * origin and the server attaches the credential.
 */
const BFF = "/v1/bff"

/**
 * `credentials: "same-origin"` on every authenticated call.
 *
 * Not `include`: the session cookie is `__Host-`-prefixed and `SameSite=Strict`, so it is
 * same-origin by construction, and asking for `include` would only widen what the browser is
 * willing to attach.
 */
const WITH_SESSION: RequestInit = { credentials: "same-origin" }

export const session = {
  /** Trade the key for a cookie. `console` picks which allowlist the proxy will enforce. */
  signIn: async (secretKey: string, consoleKind: "tenant" | "admin" = "tenant"): Promise<void> => {
    await unwrap(
      await fetch(`${API_BASE}${BFF}/session`, {
        ...WITH_SESSION,
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ secret_key: secretKey, console: consoleKind }),
      }),
    )
  },
  /** Are we signed in? Asked of the server, because the browser holds nothing it can inspect. */
  current: async (): Promise<{ tenant_id: string; console: string } | null> => {
    const resp = await fetch(`${API_BASE}${BFF}/session`, WITH_SESSION)
    if (resp.status === 401) return null
    return (await unwrap(resp)) as { tenant_id: string; console: string }
  },
  /** Sign out. The server drops the session; clearing a cookie alone would be theatre. */
  signOut: async (): Promise<void> => {
    await fetch(`${API_BASE}${BFF}/session`, { ...WITH_SESSION, method: "DELETE" }).catch(() => {
      // A failed sign-out must still clear the UI. The session expires on its own, and leaving
      // somebody staring at a console they think they left is the worse outcome.
    })
  },
}

export async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  return (await unwrap(
    await fetch(`${API_BASE}${BFF}/api${path}${query(params)}`, WITH_SESSION),
  )) as T
}

export async function post<T>(path: string, body?: unknown): Promise<T> {
  return (await unwrap(
    await fetch(`${API_BASE}${BFF}/api${path}`, {
      ...WITH_SESSION,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  )) as T
}

/** For the CSV exports, which come back as a file rather than as JSON. */
export async function getText(path: string, params?: Record<string, unknown>): Promise<string> {
  const resp = await fetch(`${API_BASE}${BFF}/api${path}${query(params)}`, WITH_SESSION)
  if (!resp.ok) await unwrap(resp)
  return await resp.text()
}

// ── wire types ─────────────────────────────────────────────────────────────

export type Standing = "reported" | "verified"
export type Tier = "low" | "moderate" | "elevated" | "high" | "insufficient_data"
export type Decision = "allow" | "review" | "deny"

/**
 * Ascending risk. `insufficient_data` is **absent on purpose** — it is the absence of an
 * assessment rather than a rung, so nothing that sorts by this can render it as the safe end.
 * Anything displaying it must handle it as its own case, which is what forces the question.
 */
export const TIER_ORDER: readonly Tier[] = ["low", "moderate", "elevated", "high"]

export interface StandingCounts {
  reported: number
  verified: number
  total: number
}

export interface Rule {
  name: string
  detail: string
}

export interface Preview {
  title: string
  description: string
  caveat: string
}

export interface Incident {
  incident_id: string
  slug: string
  title: string
  narrative: string
  standing: Standing
  resolution: string
  resolution_note: string | null
  resolution_asserted_by: string | null
  consumer_statements: { statement: string; filed_at: string }[]
  subject: Record<string, string | null>
  classification: { reason_class: string; reason_code: string; severity: number; fault: string }
  reporter_kind: string
  occurred_at: string | null
  reported_at: string
  evidence_url: string | null
  adjudication_state: string
  evidence: { present: boolean; note: string }
  subject_claimed: boolean
  open_disputes: number
  preview: Preview | null
}

/**
 * A page of the catalog.
 *
 * `incidents` is the ROWS, not a count — this response is a `Collection`, so the module's own
 * field name and the generic `items` alias carry the same list. The split-by-standing counts are
 * `by_standing`, and `total` is the whole filtered set rather than the page. Getting this wrong is
 * easy and expensive: an earlier version of this file typed `incidents` as the counts, which
 * compiled, rendered fine against an empty registry, and crashed the moment there was data.
 */
export interface IncidentPage {
  items: Incident[]
  incidents: Incident[]
  /** Reported vs verified across the whole filter — never summed. */
  by_standing: StandingCounts
  total: number
  resolved: number
  excluded_from_statistics: number
  standing_note: string
  limit: number
  offset: number
}

export interface ProfileIndexEntry {
  subject_kind: string
  subject_slug: string
  display_name: string
  incidents: number
  path: string
  origin: string
  tier: Tier
  verified: number
  resolved: number
}

export interface ProfileKindSection {
  subject_kind: string
  total: number
  subjects: ProfileIndexEntry[]
}

export interface RiskProfile {
  subject_kind: string
  subject_slug: string
  display_name: string
  tier: Tier
  rules: Rule[]
  basis: string[]
  incidents: StandingCounts
  counted: StandingCounts
  resolved: number
  excluded_from_statistics: number
  failure_modes: { reason_class: string; reason_code: string; count: number; verified: number }[]
  severity_mix: Record<string, number>
  subject_claimed: boolean
  origin: string
  seeded_note: string | null
  preview: Preview | null
}

export interface GateDecision {
  agent_did: string
  decision: Decision
  tier: Tier
  max_acceptable_tier: string
  reason: string
  rules: Rule[]
  reported: number
  verified: number
  resolved: number
  subjects: {
    subject_kind: string
    subject_slug: string
    decision: Decision
    tier: Tier
    reason: string
    rules: Rule[]
  }[]
  note: string
}

export interface ExposureEstimate {
  currency: string
  annual_actions: number
  value_at_risk_per_action: string
  notional_annual_exposure: string
  unmitigated_annual_exposure: string
  worst_case_single_action: string
  insurability: string
  insurability_detail: string
  derivation: string[]
  gate_requirements: { dimension: string; need: number; detail: string }[]
  subject_tiers: { subject_kind: string; subject_slug: string; tier: Tier }[]
  no_price_note: string
  tier_note: string
}

export interface RiskReport {
  report_id: string
  share_url: string
  generated_at: string
  headline: string
  tier: Tier
  subjects: {
    subject_kind: string
    subject_slug: string
    display_name: string
    tier: Tier
    headline: string
    open_disputes: number
    incidents: StandingCounts
    counted: StandingCounts
    resolved: number
    rules: Rule[]
  }[]
  snapshot_note: string
  standing_note: string
  preview: Preview | null
}

export interface StateOfRisk {
  period: string
  generated_at: string
  incidents: StandingCounts
  counted: StandingCounts
  resolution_rate: number
  verified_share: number
  failure_modes: { reason_class: string; reason_code: string; count: number; verified: number }[]
  severity_mix: Record<string, number>
  by_framework: SubjectStanding[]
  by_model: SubjectStanding[]
  by_category: SubjectStanding[]
  by_period: { period: string; filed: number; verified: number }[]
  health: {
    verified_claims: number
    open_disputes: number
    resolved_incidents: number
    excluded_from_statistics: number
  }
  methodology: string
}

export interface SubjectStanding {
  subject_kind: string
  subject_slug: string
  display_name: string
  incidents: number
  tier: Tier
  path: string
}

export interface Dispute {
  dispute_id: string
  incident_id: string
  grounds: string
  statement: string
  by_subject_owner: boolean
  status: string
  filed_at: string
  resolved_at: string | null
  due_at: string | null
  extended: boolean
  overdue: boolean
  notice_state: string
  frivolous: boolean
  relationship: string | null
}

/**
 * The receipt for a filed dispute.
 *
 * `incident_resolution_changed` is the field that matters and the one a naive client drops: it is
 * false for every unverified dispute, and saying so is what stops a filer believing they have
 * removed an incident when they have only annotated it.
 */
export interface DisputeFiled {
  dispute: Dispute
  incident_resolution_changed: boolean
  effect_note: string
}

export interface Tenant {
  tenant_id: string
  name: string
  status: string
  created_at: string
  authoxi_tenant_id: string | null
  scopes: string[]
}

export interface FleetAgent {
  agent_did: string
  level: number
  label: string
  status: string
  decision: string
  decisions: number
  agent_faults: number
  security_faults: number
  fault_rate: string
  completeness_tier: string | null
}

export interface Score {
  agent_did: string
  level: number
  label: string
  status: string
  decisions: number
  agent_faults: number
  security_faults: number
  fault_rate: string
  reasons: string[]
  completeness_tier: string | null
  assurance_counts: Record<string, number>
  scores_from: string
  counted: number
  uncounted: number
  next_level: number | null
  next_level_detail: string | null
}

export interface AgentEvent {
  event_id: string | null
  ts: string | null
  action_type: string | null
  reason_class: string | null
  reason_code: string | null
  severity: number | null
  loss_amount: string | null
  currency: string | null
  fault: string | null
  verified: boolean
}

export interface EvidenceSummary {
  events_held: number
  events_verified: number
  agents: number
  admitted_agents: number
  last_sync: { watermark: string | null }
}

export interface GateStatus {
  is_open: boolean
  labeled_losses: number
  threshold: number
  reasons: string[]
  dimensions: { name: string; have: number; need: number; met: boolean }[]
  rationale: string
}

export interface Virality {
  activation: Record<string, unknown>
  sharers: number
  referral_impressions: number
  referred_participants: number
  k: number | null
  readiness: string
  detail: string
  min_sharers: number
  gate: string
  measurement_note: string
}

export interface Lead {
  email: string
  segment: string
  source: string
  score: number
  warm: boolean
  consented: boolean
  context: string | null
  captured_at: string
  first_seen_at: string | null
  touch_count: number
  reasons: string[]
  handling_note: string | null
}

// ── the calls each surface makes ───────────────────────────────────────────

export const registry = {
  incidents: (params?: Record<string, unknown>) => publicGet<IncidentPage>("/incidents", params),
  incident: (ref: string) => publicGet<Incident>(`/incidents/${encodeURIComponent(ref)}`),
  disputes: (ref: string) =>
    publicGet<Dispute[]>(`/incidents/${encodeURIComponent(ref)}/disputes`),
  taxonomy: () => publicGet<Record<string, unknown>>("/incident-taxonomy"),
  profileKinds: () => publicGet<{ kinds: ProfileKindSection[] }>("/risk-profiles"),
  profileIndex: (kind: string, params?: Record<string, unknown>) =>
    publicGet<{ subjects: ProfileIndexEntry[] }>(`/risk-profiles/${kind}`, params),
  profile: (kind: string, slug: string) =>
    publicGet<RiskProfile>(`/risk-profiles/${kind}/${encodeURIComponent(slug)}`),
  stateOfRisk: (params?: Record<string, unknown>) =>
    publicGet<StateOfRisk>("/state-of-agent-risk", params),
  gate: (policy: Record<string, unknown>) => publicPost<GateDecision>("/risk-gate", policy),
  exposure: (inputs: Record<string, unknown>) =>
    publicPost<ExposureEstimate>("/exposure-estimate", inputs),
  pullReport: (subjects: Record<string, unknown>) =>
    publicPost<RiskReport>("/risk-reports", subjects),
  report: (id: string) => publicGet<RiskReport>(`/risk-reports/${encodeURIComponent(id)}`),
  fileIncident: (body: Record<string, unknown>) =>
    publicPost<{ incident_id: string; slug: string; share_url: string }>("/incidents", body),
  dispute: (ref: string, body: Record<string, unknown>) =>
    publicPost<DisputeFiled>(`/incidents/${encodeURIComponent(ref)}/disputes`, body),
  claim: (body: Record<string, unknown>) =>
    publicPost<Record<string, unknown>>("/incident-claims", body),
  statement: (ref: string, body: Record<string, unknown>) =>
    publicPost<Record<string, unknown>>(`/incidents/${encodeURIComponent(ref)}/statements`, body),
  resolution: (ref: string, body: Record<string, unknown>) =>
    publicPost<Incident>(`/incidents/${encodeURIComponent(ref)}/resolution`, body),
  capture: (body: Record<string, unknown>) =>
    publicPost<{ note: string | null }>("/captures", body),
  forget: (email: string) => publicPost<{ forgotten: boolean }>("/captures/forget", { email }),
}

export const tenantApi = {
  whoami: () => get<Tenant>("/tenant"),
  fleet: (params?: Record<string, unknown>) =>
    get<{ agents: FleetAgent[]; items?: FleetAgent[] }>("/agents/summary", params),
  score: (did: string) => get<Score>(`/agents/${encodeURIComponent(did)}/score`),
  events: (did: string, params?: Record<string, unknown>) =>
    get<{ events: AgentEvent[]; total: number }>(
      `/agents/${encodeURIComponent(did)}/events`,
      params,
    ),
  trackRecord: (did: string) =>
    get<Record<string, unknown>>(`/agents/${encodeURIComponent(did)}/track-record`),
  evidenceSummary: () => get<EvidenceSummary>("/evidence/summary"),
  sync: () => post<Record<string, unknown>>("/evidence/sync"),
  admitPassport: (passport: string) =>
    post<Record<string, unknown>>("/agent-passports", { passport }),
  baseRates: () => get<Record<string, unknown>>("/risk-data/base-rates"),
  severityDistribution: () => get<Record<string, unknown>>("/risk-data/severity-distribution"),
  riskExposure: () => get<Record<string, Record<string, string | number>>>("/risk-data/exposure"),
  cohorts: (dimension: string) => get<Record<string, unknown>>(`/risk-data/cohorts/${dimension}`),
  gateStatus: () => get<GateStatus>("/cover/gate-status"),
}

export const adminApi = {
  whoami: () => get<Tenant>("/tenant"),
  queue: (params?: Record<string, unknown>) =>
    get<{ incidents: Incident[]; backlog: number }>("/adjudication/queue", params),
  submit: (id: string) => post<Incident>(`/adjudication/${encodeURIComponent(id)}/submit`),
  verdict: (id: string, body: Record<string, unknown>) =>
    post<Incident>(`/adjudication/${encodeURIComponent(id)}/verdict`, body),
  disputes: (params?: Record<string, unknown>) =>
    get<{ disputes: Dispute[]; backlog: number; overdue: number }>(
      "/adjudication/disputes",
      params,
    ),
  extend: (id: string) => post<Dispute>(`/adjudication/disputes/${encodeURIComponent(id)}/extend`),
  notice: (id: string) => post<Dispute>(`/adjudication/disputes/${encodeURIComponent(id)}/notice`),
  sweep: () => post<Record<string, unknown>>("/adjudication/disputes/sweep"),
  verifyClaim: (id: string) =>
    post<Record<string, unknown>>(`/incident-claims/${encodeURIComponent(id)}/verify`),
  leads: (params?: Record<string, unknown>) =>
    get<{ leads: Lead[]; total?: number }>("/leads", params),
  leadsCsv: () => getText("/leads", { format: "csv" }),
  virality: () => get<Virality>("/virality"),
}
